import React, { useMemo } from 'react'
import { usePageLayout } from '../hooks/usePageLayout'
import { Locale, useLang, translate } from '../components/Locale'
import './Privacy.css'

function Privacy() {
  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Privacy Policy',
        zh_cn: '隐私政策',
        zh_hk: '私隱政策',
      },
    }),
    [],
  )

  usePageLayout(layoutOptions)

  const { lang } = useLang()

  const lastUpdated = '2025-12-27'

  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <h1>
          <Locale en="Privacy Policy" zh_cn="隐私政策" zh_hk="私隱政策" />
        </h1>
        <p className="last-updated">
          <Locale
            en={`Last Updated: ${lastUpdated}`}
            zh_cn={`最后更新：${lastUpdated}`}
            zh_hk={`最後更新：${lastUpdated}`}
          />
        </p>

        <section>
          <h2>
            <Locale en="Introduction" zh_cn="介绍" zh_hk="介紹" />
          </h2>
          <p>
            <Locale
              en="This Privacy Policy describes how we collect, use, and protect your personal information when you use our service."
              zh_cn="本隐私政策描述了我们如何收集、使用和保护您在使用我们服务时的个人信息。"
              zh_hk="本私隱政策描述了我們如何收集、使用和保護您在使用我們服務時的個人資料。"
            />
          </p>
        </section>

        <section>
          <h2>
            <Locale
              en="Information We Collect"
              zh_cn="我们收集的信息"
              zh_hk="我們收集的資料"
            />
          </h2>

          <h3>
            <Locale
              en="Account Information"
              zh_cn="账户信息"
              zh_hk="帳戶資料"
            />
          </h3>
          <ul>
            <li>
              <Locale
                en="Email address: Required for account registration and authentication. We use your email to send welcome emails and account-related notifications."
                zh_cn="电子邮件地址：账户注册和身份验证所必需。我们使用您的电子邮件发送欢迎邮件和账户相关通知。"
                zh_hk="電子郵件地址：帳戶註冊和身份驗證所必需。我們使用您的電子郵件發送歡迎郵件和帳戶相關通知。"
              />
            </li>
            <li>
              <Locale
                en="Password: Stored securely using industry-standard hashing (bcrypt). We never store passwords in plain text."
                zh_cn="密码：使用行业标准哈希算法（bcrypt）安全存储。我们从不以明文形式存储密码。"
                zh_hk="密碼：使用行業標準雜湊演算法（bcrypt）安全儲存。我們從不以明文形式儲存密碼。"
              />
            </li>
            <li>
              <Locale
                en="To send verification codes, we transmit your email address to your email provider and your phone number to SMS service providers. This is required for account verification. SMS messages are delivered via Wavecell."
                zh_cn="为了发送验证码，我们会将您的电子邮件地址传送给您的电子邮件提供商，并将您的电话号码传送给短信服务提供商。这是账户验证所必需的。短信通过 Wavecell 发送。"
                zh_hk="為了發送驗證碼，我們會將您的電子郵件地址傳送給您的電子郵件提供商，並將您的電話號碼傳送給短信服務提供商。這是帳戶驗證所必需的。簡訊由 Wavecell 提供。"
              />
            </li>
          </ul>

          <h3>
            <Locale
              en="Analytics and Location Data"
              zh_cn="分析和位置数据"
              zh_hk="分析和位置資料"
            />
          </h3>
          <p>
            <Locale
              en="We collect general geographic location information (country and city level) derived from IP addresses for analytics purposes. We use findip.net API service to obtain this geolocation data. Important: We do not store your actual IP address - only the derived location information (country and city) is stored. This data helps us understand usage patterns and improve our services. Location data is aggregated and anonymized for statistical analysis."
              zh_cn="我们收集从IP地址派生的通用地理位置信息（国家和城市级别）用于分析目的。我们使用 findip.net API 服务来获取此地理位置数据。重要提示：我们不存储您的实际IP地址 - 仅存储派生的位置信息（国家和城市）。这些数据帮助我们了解使用模式并改进我们的服务。位置数据经过聚合和匿名化处理，用于统计分析。"
              zh_hk="我們收集從IP地址衍生的通用地理位置資料（國家和城市級別）用於分析目的。我們使用 findip.net API 服務來獲取此地理位置資料。重要提示：我們不儲存您的實際IP地址 - 僅儲存衍生的位置資料（國家和城市）。這些資料幫助我們了解使用模式並改進我們的服務。位置資料經過聚合和匿名化處理，用於統計分析。"
            />
          </p>

          <h3>
            <Locale en="Usage Data" zh_cn="使用数据" zh_hk="使用資料" />
          </h3>
          <p>
            <Locale
              en="We automatically collect certain information when you use our service, including API requests and responses (for debugging and improvement), browser/device information (user agent), timestamps, and request duration. This data is linked to your user account if you are logged in."
              zh_cn="当您使用我们的服务时，我们会自动收集某些信息，包括API请求和响应（用于调试和改进）、浏览器/设备信息（用户代理）、时间戳和请求持续时间。如果您已登录，此数据会链接到您的用户账户。"
              zh_hk="當您使用我們的服務時，我們會自動收集某些資料，包括API請求和響應（用於調試和改進）、瀏覽器/設備資料（用戶代理）、時間戳和請求持續時間。如果您已登入，此資料會連結到您的用戶帳戶。"
            />
          </p>

          <h3>
            <Locale
              en="Optional Information"
              zh_cn="可选信息"
              zh_hk="可選資料"
            />
          </h3>
          <p>
            <Locale
              en="You may optionally provide demographic information (region, occupation, company size, industry, etc.) to help us improve our services. This information is not required."
              zh_cn="您可以选择提供人口统计信息（地区、职业、公司规模、行业等）以帮助我们改进服务。此信息不是必需的。"
              zh_hk="您可以選擇提供人口統計資料（地區、職業、公司規模、行業等）以幫助我們改進服務。此資料不是必需的。"
            />
          </p>
        </section>

        <section>
          <h2>
            <Locale
              en="How We Use Information"
              zh_cn="我们如何使用信息"
              zh_hk="我們如何使用資料"
            />
          </h2>
          <p>
            <Locale
              en="We use the information we collect to:"
              zh_cn="我们使用收集的信息来："
              zh_hk="我們使用收集的資料來："
            />
          </p>
          <ul>
            <li>
              <Locale
                en="Provide and maintain our service"
                zh_cn="提供和维护我们的服务"
                zh_hk="提供和維護我們的服務"
              />
            </li>
            <li>
              <Locale
                en="Authenticate your account and manage your access"
                zh_cn="验证您的账户并管理您的访问权限"
                zh_hk="驗證您的帳戶並管理您的訪問權限"
              />
            </li>
            <li>
              <Locale
                en="Send you welcome emails and account-related notifications"
                zh_cn="向您发送欢迎邮件和账户相关通知"
                zh_hk="向您發送歡迎郵件和帳戶相關通知"
              />
            </li>
            <li>
              <Locale
                en="Analyze usage patterns to improve our services"
                zh_cn="分析使用模式以改进我们的服务"
                zh_hk="分析使用模式以改進我們的服務"
              />
            </li>
            <li>
              <Locale
                en="Generate aggregated statistics (e.g., visits by country/city)"
                zh_cn="生成聚合统计（例如，按国家/城市的访问量）"
                zh_hk="生成聚合統計（例如，按國家/城市的訪問量）"
              />
            </li>
            <li>
              <Locale
                en="Debug and troubleshoot technical issues"
                zh_cn="调试和排查技术问题"
                zh_hk="調試和排查技術問題"
              />
            </li>
          </ul>
        </section>

        <section>
          <h2>
            <Locale en="Data Storage" zh_cn="数据存储" zh_hk="資料儲存" />
          </h2>

          <h3>
            <Locale en="Data Security" zh_cn="数据安全" zh_hk="資料安全" />
          </h3>
          <ul>
            <li>
              <Locale
                en="Passwords are hashed using bcrypt before storage"
                zh_cn="密码在存储前使用bcrypt进行哈希处理"
                zh_hk="密碼在儲存前使用bcrypt進行雜湊處理"
              />
            </li>
            <li>
              <Locale
                en="We use industry-standard security practices to protect your data"
                zh_cn="我们使用行业标准的安全实践来保护您的数据"
                zh_hk="我們使用行業標準的安全實踐來保護您的資料"
              />
            </li>
            <li>
              <Locale
                en="Your email address is stored securely in our database"
                zh_cn="您的电子邮件地址安全地存储在我们的数据库中"
                zh_hk="您的電子郵件地址安全地儲存在我們的資料庫中"
              />
            </li>
          </ul>

          <h3>
            <Locale en="Data Retention" zh_cn="数据保留" zh_hk="資料保留" />
          </h3>
          <ul>
            <li>
              <Locale
                en="Account information is retained for as long as your account is active"
                zh_cn="账户信息在您的账户处于活动状态期间保留"
                zh_hk="帳戶資料在您的帳戶處於活動狀態期間保留"
              />
            </li>
            <li>
              <Locale
                en="Location data is aggregated and anonymized"
                zh_cn="位置数据经过聚合和匿名化处理"
                zh_hk="位置資料經過聚合和匿名化處理"
              />
            </li>
            <li>
              <Locale
                en="Log data may be retained for debugging and analytics purposes"
                zh_cn="日志数据可能保留用于调试和分析目的"
                zh_hk="日誌資料可能保留用於調試和分析目的"
              />
            </li>
          </ul>
        </section>

        <section>
          <h2>
            <Locale en="Do Not Track" zh_cn="请勿追踪" zh_hk="請勿追蹤" />
          </h2>
          <p>
            <Locale
              en="We honor the Do Not Track (DNT) browser preference. When you enable DNT in your browser settings, we automatically disable geolocation data and user agent collection for all your visits. This preference is respected immediately and requires no additional action on your part."
              zh_cn="我们尊重「请勿追踪」(DNT) 浏览器偏好设置。当您在浏览器设置中启用 DNT 后，我们会自动停止为您的所有访问收集地理位置数据和用户代理信息。此偏好设置会立即生效，无需您采取任何额外操作。"
              zh_hk="我們尊重「請勿追蹤」(DNT) 瀏覽器偏好設定。當您在瀏覽器設置中啟用 DNT 後，我們會自動停止為您的所有訪問收集地理位置資料和用戶代理資訊。此偏好設定會立即生效，無需您採取任何額外操作。"
            />
          </p>
        </section>

        <section>
          <h2>
            <Locale en="Your Rights" zh_cn="您的权利" zh_hk="您的權利" />
          </h2>
          <p>
            <Locale
              en="Under GDPR (EU) and CCPA (California), you have the right to:"
              zh_cn="根据GDPR（欧盟）和CCPA（加利福尼亚州），您有权："
              zh_hk="根據GDPR（歐盟）和CCPA（加利福尼亞州），您有權："
            />
          </p>
          <ul>
            <li>
              <Locale
                en="Access your personal data"
                zh_cn="访问您的个人数据"
                zh_hk="訪問您的個人資料"
              />
            </li>
            <li>
              <Locale
                en="Rectify inaccurate data"
                zh_cn="纠正不准确的数据"
                zh_hk="糾正不準確的資料"
              />
            </li>
            <li>
              <Locale
                en="Request deletion of your data"
                zh_cn="请求删除您的数据"
                zh_hk="請求刪除您的資料"
              />
            </li>
            <li>
              <Locale
                en="Object to processing of your data"
                zh_cn="反对处理您的数据"
                zh_hk="反對處理您的資料"
              />
            </li>
            <li>
              <Locale
                en="Data portability"
                zh_cn="数据可移植性"
                zh_hk="資料可移植性"
              />
            </li>
          </ul>
          <p>
            <Locale
              en="To exercise these rights, please contact us using the information below."
              zh_cn="要行使这些权利，请使用下面的信息联系我们。"
              zh_hk="要行使這些權利，請使用下面的資料聯絡我們。"
            />
          </p>
        </section>

        <section>
          <h2>
            <Locale en="Contact" zh_cn="联系方式" zh_hk="聯絡方式" />
          </h2>
          <p>
            <Locale
              en="If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at: info@feelingss.io"
              zh_cn="如果您对此隐私政策有疑问或希望行使您的权利，请通过以下方式联系我们：info@feelingss.io"
              zh_hk="如果您對此私隱政策有疑問或希望行使您的權利，請通過以下方式聯絡我們：info@feelingss.io"
            />
          </p>
        </section>
      </div>
    </div>
  )
}

export default Privacy
